import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Home, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function CompanyManagement() {
  const { user } = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '', address: '', contact_email: '', contact_phone: '', plan: 'standard'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/companies`, { withCredentials: true });
      setCompanies(r.data);
    } catch { toast.error('Failed to load companies'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/companies`, newCompany, { withCredentials: true });
      toast.success(user?.role === 'SUPERADMIN' ? 'Company created' : 'Sub-tenant company created');
      setShowNew(false);
      setNewCompany({ name: '', address: '', contact_email: '', contact_phone: '', plan: 'standard' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const title = user?.role === 'SUPERADMIN' ? 'All Companies (Platform)' : 'Your Company & Sub-Tenants';
  const sub = user?.role === 'SUPERADMIN'
    ? 'As SKG Apps platform admin, manage all client companies'
    : 'Manage your company and add sub-tenant companies below';

  return (
    <Layout>
      <div data-testid="companies-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{title}</h1>
            <p className="text-sm text-muted-foreground">{sub}</p>
          </div>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild>
              <Button data-testid="new-company-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New Company</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{user?.role === 'SUPERADMIN' ? 'Create Client Company' : 'Create Sub-Tenant Company'}</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label>Company Name</Label><Input data-testid="company-name-input" value={newCompany.name} onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })} /></div>
                <div><Label>Address</Label><Textarea rows={2} value={newCompany.address} onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Contact Email</Label><Input type="email" value={newCompany.contact_email} onChange={(e) => setNewCompany({ ...newCompany, contact_email: e.target.value })} /></div>
                  <div><Label>Contact Phone</Label><Input value={newCompany.contact_phone} onChange={(e) => setNewCompany({ ...newCompany, contact_phone: e.target.value })} /></div>
                </div>
                <div><Label>Plan</Label>
                  <Select value={newCompany.plan} onValueChange={(v) => setNewCompany({ ...newCompany, plan: v })}>
                    <SelectTrigger data-testid="company-plan-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="submit-company-btn" onClick={handleCreate} disabled={!newCompany.name} className="w-full bg-teal-600 hover:bg-teal-700">Create Company</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((c) => (
              <Card key={c.id} data-testid={`company-card-${c.id}`} className={`p-5 ${c.id === user?.company_id ? 'ring-2 ring-teal-500' : ''}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-teal-100 rounded-lg"><Home className="w-5 h-5 text-teal-700" /></div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' : c.plan === 'pro' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}`}>{c.plan}</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">{c.name}</h3>
                {c.id === user?.company_id && <span className="inline-block mb-2 text-[10px] font-bold uppercase tracking-wide text-teal-600">Your Company</span>}
                {c.parent_company_id && <span className="inline-block mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Sub-tenant</span>}
                <div className="space-y-1 text-sm text-muted-foreground">
                  {c.address && <p className="text-xs text-muted-foreground">{c.address}</p>}
                  {c.contact_email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{c.contact_email}</div>}
                  {c.contact_phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{c.contact_phone}</div>}
                </div>
              </Card>
            ))}
            {companies.length === 0 && (
              <Card className="p-10 text-center col-span-full">
                <Home className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No companies</p>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
